import React, { useState } from 'react'
import './ManageBilling.css'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import { Divider, Typography } from '@mui/material'
import {
    formatAmounts,
    formatDate,
    formatDateNoHour,
    formatHour,
    formatSessionState,
    getChargingPowerTypeDisplay,
} from './../utilities/generalUtils'
import './UserBillingGrid/UserBillingGrid.css'
import ChargingHistoryModal from '../components/chargingHistory/ChargingHistoryModal'

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        backgroundColor: 'var(--gp-card)',
        color: 'var(--gp-ink-mute)',
        fontFamily: 'var(--gp-font)',
        borderBottom: '1px solid var(--gp-hairline)',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        fontWeight: 400,
        color: 'var(--gp-ink)',
        fontFamily: 'var(--gp-font)',
        borderBottom: '1px solid var(--gp-hairline)',
    },
}))

const StyledTableRow = styled(TableRow)(() => ({
    '&:hover': {
        backgroundColor: 'var(--gp-surface)',
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}))

const monoSx = { fontFamily: 'var(--gp-mono)' }

const ChargingHistory = (props) => {
    const [openHistoryModal, setOpenHistoryModal] = useState(false)
    const [openHistoryModalSessionUUID, setOpenHistoryModalSessionUUID] = useState()
    const [openHistoryModalSessionDate, setOpenHistoryModalSessionDate] = useState()
    const [openHistoryModalSessionPeakWatts, setOpenHistoryModalSessionPeakWatts] = useState()
    const [openHistoryModalSessionPowerOutages, setOpenHistoryModalSessionPowerOutages] = useState([])

    const fmtUSD = React.useMemo(
        () => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }),
        []
    )

    if (props.isGuest) {
        return (
            <div className="billing-table-container">
                {props.userChargingHistory?.items?.length > 0 ? (
                    <>
                        {props.userChargingHistory.items.map((row, idx) => {
                            const s = row.session || {}
                            const h = s?.duration?.hours ?? 0
                            const m = s?.duration?.minutes ?? 0
                            const energy = s?.powerConsumedInKwh
                                ? `${s.powerConsumedInKwh.power}${s.powerConsumedInKwh.unit}`
                                : '0kWh'
                            const cost = s?.chargingCost != null ? fmtUSD.format(s.chargingCost) : '-'

                            return (
                                <React.Fragment key={s?.uuid || idx} >
                                    <div style={{ padding: '12px 16px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }} color="text.secondary">
                                                {formatDateNoHour(s?.startedAt)}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }} color="text.secondary">
                                                {formatHour(s?.startedAt)}
                                            </Typography>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body1" sx={{ fontSize: '1.15rem' }} color="text.primary">{h}h {m}m</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.15rem' }} color="text.primary">{energy}</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.15rem' }} color="text.primary">{cost}</Typography>
                                        </div>
                                    </div>
                                    {idx < props.userChargingHistory.items.length - 1 && <Divider />}
                                </React.Fragment>
                            )
                        })}
                    </>
                ) : (
                    <div className="noInfoAvailableText">
                        You have no charging history
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="billing-table-container">
            {props.userChargingHistory?.items?.length > 0 ? (
                <>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ boxShadow: 'none', background: 'transparent', borderRadius: 0 }}
                    >
                        <Table
                            sx={{ minWidth: 650 }}
                            aria-label="caption table"
                            data-testid="cypress-billing-delete-chargeHistory-table"
                        >
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Vehicle</StyledTableCell>
                                    <StyledTableCell>
                                        Range Requested
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Started At
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Completed At
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Session Length
                                    </StyledTableCell>
                                    <StyledTableCell>Total kWh</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    {!props.isRecurring && (
                                        <StyledTableCell sx={{ textAlign: 'right' }}>
                                            Total Charging Cost
                                        </StyledTableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {props.userChargingHistory?.items.map((row) => (
                                    <StyledTableRow
                                        key={row.name}
                                        onClick={() => {
                                            setOpenHistoryModal(true)
                                            setOpenHistoryModalSessionUUID(row.session?.uuid)
                                            setOpenHistoryModalSessionDate(row.session?.startedAt)
                                            if (row.session?.powerDraw?.peakWatts) {
                                                const peakWatts = parseFloat(row.session.powerDraw.peakWatts / 1000).toFixed(1)
                                                const chargeType = getChargingPowerTypeDisplay(row.session.chargeType, props.powerType, peakWatts)
                                                setOpenHistoryModalSessionPeakWatts(chargeType)
                                            }
                                            if (row?.powerOutages) {
                                                setOpenHistoryModalSessionPowerOutages(row.powerOutages)
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {row.userVehicle?.name}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                            sx={monoSx}
                                        >
                                            {row.session?.rangeRequired + 'mi'}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                            sx={monoSx}
                                        >
                                            {formatDate(row.session?.startedAt)}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                            sx={monoSx}
                                        >
                                            {formatDate(
                                                row.session?.completedAt
                                            )}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                            sx={monoSx}
                                        >
                                            {row.session?.duration?.hours +
                                                'hrs ' +
                                                row.session?.duration?.minutes +
                                                'min'}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                            sx={monoSx}
                                        >
                                            {row.session?.powerConsumedInKwh
                                                ?.power +
                                                row.session?.powerConsumedInKwh
                                                    ?.unit}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {formatSessionState(
                                                row.session?.state
                                            )}
                                        </StyledTableCell>
                                        {!props.isRecurring && (
                                            <StyledTableCell
                                                component="th"
                                                scope="row"
                                                sx={{ ...monoSx, textAlign: 'right' }}
                                            >
                                                {row.session?.chargingCost
                                                    ? `$${row.session?.chargingCost}`
                                                    : '—'}
                                            </StyledTableCell>
                                        )}
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            ) : (
                <div className="noInfoAvailableText">
                    You have no charging history
                </div>
            )}

            {/** modal */}
            <ChargingHistoryModal
                open={openHistoryModal}
                sessionDate={openHistoryModalSessionDate}
                sessionUUID={openHistoryModalSessionUUID}
                sessionPeakWatts={openHistoryModalSessionPeakWatts}
                sessionPowerOutages={openHistoryModalSessionPowerOutages}
                handleClose={() => {
                    setOpenHistoryModal(false)
                    setOpenHistoryModalSessionUUID(null)
                    setOpenHistoryModalSessionDate(null)
                    setOpenHistoryModalSessionPeakWatts(null)
                    setOpenHistoryModalSessionPowerOutages([])
                }}
            />
        </div>
    )
}

export default ChargingHistory
